import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPerformanceData } from '../redux/reducer';
import PerformanceGrowthChart from './PerformanceGrowthChart';
import PerformanceGrowthTable, {
  getPerformanceTabData,
  PerformanceTabs,
} from './PerformanceGrowthTable';
import CalendarPerformanceChart from './PerformanceChart';
import PeriodSelector from './PeriodSelector';
import Loader from './Loader';
import i18next from '../translations/i18n';

const formatDate = dateInput => {
  const date = new Date(dateInput);
  const formatted = date.toLocaleDateString('en-GB'); // "13/08/2024"
  return formatted.replace(/\//g, '-'); // "13-08-2024"
};

const EUPerformanceTabs = ({
  activeTab,
  setActiveTab,
  activeTableTab,
  setActiveTableTab,
  tableData,
  gro10k,
  selectedPeriodFilteredData,
  tabular,
  entityInceptionDt,
  ucitsFootenote,
}) => {
  const tabRowRef = useRef(null);
  const [inlineTabs, setInlineTabs] = useState(false);

  useLayoutEffect(() => {
    const row = tabRowRef.current;
    const updateLayout = () => {
      const parentTabs = row.querySelector('.parent-tabs');
      const childTabs = row.querySelector('.table-tabs, .period-tabs');
      if (!childTabs) {
        setInlineTabs(false);
        return;
      }

      const totalWidth = elements =>
        [...elements].reduce((width, element) => width + element.getBoundingClientRect().width, 0);
      const dates = row.querySelector('.wt-charts-date');
      const dateGap = dates ? parseFloat(getComputedStyle(dates).columnGap) || 0 : 0;
      const dateWidth = dates
        ? totalWidth(dates.children) + dateGap * (dates.children.length - 1)
        : 0;
      const neededWidth =
        totalWidth(parentTabs.children) + Math.max(totalWidth(childTabs.children), dateWidth);

      setInlineTabs(neededWidth <= row.clientWidth);
    };

    const observer = new ResizeObserver(updateLayout);
    observer.observe(row);
    row.querySelectorAll('.nav-tabs li, .wt-charts-input').forEach(tab => observer.observe(tab));
    updateLayout();

    return () => observer.disconnect();
  });

  return (
    <>
      <div
        className={`nav-header ${activeTab === 'table' ? 'nav-header--table' : ''} ${inlineTabs ? 'nav-header--inline' : ''}`}
      >
        <div className="tab-row" ref={tabRowRef}>
          <ul className="nav nav-tabs parent-tabs">
            <li className={`tab-responsive ${activeTab === 'graph' ? 'active' : ''}`}>
              <button type="button" className="nav-link" onClick={() => setActiveTab('graph')}>
                {i18next.t('PERFORMANCE_LINE_GRAPH')}
              </button>
            </li>
            <li className={`tab-responsive ${activeTab === 'table' ? 'active' : ''}`}>
              <button type="button" className="nav-link" onClick={() => setActiveTab('table')}>
                {i18next.t('PERFORMANCE_TABLES')}
              </button>
            </li>
          </ul>
          {activeTab === 'table' && (
            <PerformanceTabs
              activeTab={activeTableTab}
              setActiveTab={setActiveTableTab}
              annualReturns={tableData.annualReturns}
              cumulativeReturns={tableData.cumulativeReturns}
              calenderPerformance={tableData.calenderPerformance}
            />
          )}
          {activeTab === 'graph' && selectedPeriodFilteredData.length > 0 && <PeriodSelector />}
        </div>
      </div>
      <div className="tab-content">
        {activeTab === 'graph' ? (
          <>
            <section className="chart-wrapper">
              <PerformanceGrowthChart />
            </section>
            {gro10k.length > 0 && (
              <section className="calendar-performance-chart m-top">
                <CalendarPerformanceChart />
              </section>
            )}
          </>
        ) : (
          <PerformanceGrowthTable activeTab={activeTableTab} tableData={tableData} />
        )}
        {(activeTab === 'graph' ? gro10k.length > 0 : tabular.length > 0) && (
          <div className="disclosure">
            <p className="disclaimer-text">
              <span>
                {activeTab === 'graph'
                  ? i18next.t('DISCLAIMER')
                  : i18next.t('DISCLAIMER_TABLES', { defaultValue: i18next.t('DISCLAIMER') })}
              </span>
              &nbsp;
              <span>
                {i18next
                  .t('INCEPTION_DATE')
                  .replace('{inceptionDt}', formatDate(entityInceptionDt))}
              </span>
            </p>
            <p className="disclaimer-text">
              <span>{ucitsFootenote}</span>
            </p>
          </div>
        )}
      </div>
    </>
  );
};

const PerformanceChartContainer = () => {
  const dispatch = useDispatch();
  const {
    fundName,
    historicalPerformanceData: { tabular = [], gro10k = [] },
    selectedPeriodFilteredData = [],
    entityInceptionDt,
    ucitsFootenote,
  } = useSelector(state => state.app);
  const [activeTab, setActiveTab] = useState('graph');
  const [activeTableTab, setActiveTableTab] = useState(null);
  const isEURegion = window.performanceChart?.wtRegion !== 'US';
  const [initialLoading, setInitialLoading] = useState(isEURegion);
  const tableData = getPerformanceTabData(tabular);

  useEffect(() => {
    if (!isEURegion) return undefined;

    let isActive = true;

    const loadChart = async () => {
      try {
        await dispatch(
          getPerformanceData({ entity: window.performanceChart?.tickerCode, mode: 'gro10k' }),
        );
      } finally {
        if (isActive) setInitialLoading(false);
      }
    };

    loadChart();

    return () => {
      isActive = false;
    };
  }, [dispatch, isEURegion]);

  if (initialLoading) {
    return (
      <div className="chart-container">
        <h3 className="typ-h3">{i18next.t('PERFORMANCE_TITLE')}</h3>
        <Loader show />
      </div>
    );
  }

  if (isEURegion && gro10k.length === 0) {
    return (
      <div className="chart-container">
        <h3 className="typ-h3">{i18next.t('PERFORMANCE_TITLE')}</h3>
        <section className="calendar-performance-chart m-top">
          <CalendarPerformanceChart />
        </section>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3 className="typ-h3">{i18next.t('PERFORMANCE_TITLE')}</h3>
      <div className="typ-h4 chart-sub-title">{fundName}</div>
      {isEURegion && (
        <EUPerformanceTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeTableTab={activeTableTab || tableData.defaultTab}
          setActiveTableTab={setActiveTableTab}
          tableData={tableData}
          gro10k={gro10k}
          selectedPeriodFilteredData={selectedPeriodFilteredData}
          tabular={tabular}
          ucitsFootenote={ucitsFootenote}
          entityInceptionDt={entityInceptionDt}
        />
      )}
      {!isEURegion && (
        <section className="calendar-performance-chart m-top">
          <CalendarPerformanceChart />
        </section>
      )}
    </div>
  );
};

export default PerformanceChartContainer;
